import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useReturnViewState, useRestoreScroll, TRADES_FLOW_PREFIXES, COMPLETED_TRADES_VIEW_KEY } from '../hooks/useReturnViewState';
import Footer from '../components/Footer';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import { useNotifications } from '../contexts/NotificationContext';
import { placeholderFor } from '../utils/listingPlaceholder';

// Helper function to check if messaging is still available (30 days after completion)
const isMessagingAvailable = (trade) => {
  // Use autoCompletedAt (set when trade auto-completes) or completedAt (set manually)
  const completionTimestamp = trade.autoCompletedAt || trade.completedAt;
  
  if (!completionTimestamp) {
    console.log('⚠️ No completion timestamp found for this trade');
    return false;
  }
  
  const completedDate = completionTimestamp.toDate ? completionTimestamp.toDate() : new Date(completionTimestamp);
  const now = new Date();
  const daysSinceCompletion = (now - completedDate) / (1000 * 60 * 60 * 24);
  
  console.log(`📅 Trade completed ${daysSinceCompletion.toFixed(1)} days ago - Messaging ${daysSinceCompletion <= 30 ? 'AVAILABLE' : 'EXPIRED'}`);
  
  return daysSinceCompletion <= 30;
};

const getUserTradeStep = (trade, currentUserId) => {
  // Handle trade offer only (purchases handled in CompletedPurchases.jsx)
  const isFromUser = trade.fromUserId === currentUserId;
  const shipping = trade.shippingStatus || {};
  const userPaid = isFromUser 
    ? trade.paymentStatus?.fromUserPaid 
    : trade.paymentStatus?.toUserPaid;
  const userShipping = isFromUser ? shipping.fromUser : shipping.toUser;
  
  // Check if user has provided feedback
  const userFeedback = isFromUser ? trade.fromUserFeedback : trade.toUserFeedback;
  const hasProvidedFeedback = userFeedback && userFeedback.submittedAt;
  
  const user = {
    paid: userPaid || false,
    photoUploaded: isFromUser ? shipping.fromUserPhotos?.length > 0 : shipping.toUserPhotos?.length > 0,
    ready: isFromUser ? shipping.fromUserReady : shipping.toUserReady,
    label: userShipping?.labelPurchased || false,
    inTransit: userShipping?.inTransit || false,
    delivered: userShipping?.delivered || false,
  };
  
  // If trade is complete and user has provided feedback, show completed status
  if (trade.status === 'complete' && hasProvidedFeedback) {
    return 'Trade Completed';
  }
  
  // If trade is complete but no feedback yet, show delivered status
  if (trade.status === 'complete' && user.delivered) {
    return 'Delivered - Feedback Pending';
  }
  
  const steps = [
    { label: 'Completed Payment', check: (u) => u.paid },
    { label: 'Upload Confirmation Photo', check: (u) => u.photoUploaded },
    { label: 'Ready to Ship', check: (u) => u.ready },
    { label: 'Generate Shipping Label', check: (u) => u.label },
    // In Transit completes on delivery; the separate delivery step was folded
    // into it on the progress pages (2026-11-01). Same ladder here.
    { label: 'Item In Transit', check: (u) => u.delivered },
    { label: 'Feedback Provided', check: () => hasProvidedFeedback },
  ];
  const completedIndex = steps.findIndex((step) => !step.check(user));
  return completedIndex === -1 ? 'Trade Completed' : steps[completedIndex].label;
};

const CompletedTrades = () => {
  const { notifications, markAsRead } = useNotifications();
  const claimedNotificationIds = useRef(new Set());

  // The completed-trade alert also clears from the trade's progress page, but
  // this list is where the "Trades > Completed" tab lands. Every completed
  // trade is rendered here, so anything unread is genuinely on screen.
  useEffect(() => {
    const pending = notifications.filter(
      n => n.type === 'trade_complete' && !n.read && !claimedNotificationIds.current.has(n.id)
    );
    if (pending.length === 0) return;

    // Claim before awaiting — see the note in TradeCenter; each write pushes a
    // snapshot that re-runs this effect mid-loop.
    pending.forEach(n => claimedNotificationIds.current.add(n.id));

    (async () => {
      console.log(`📧 Marking ${pending.length} trade completion notification(s) as read on CompletedTrades page`);
      for (const notification of pending) {
        await markAsRead(notification.id);
      }
    })();
  }, [notifications, markAsRead]);

  const [completedTrades, setCompletedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  // "Put me back where I was": capture-handler save + anchored restore, same
  // rationale as AcceptedTrades (see hooks/useReturnViewState.js).
  const { scrollSnapshot, saveView } = useReturnViewState({
    storageKey: COMPLETED_TRADES_VIEW_KEY,
    flowPrefixes: TRADES_FLOW_PREFIXES,
  });
  useRestoreScroll(scrollSnapshot, !loading);
  const [usernames, setUsernames] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, notLoggedIn]);

  const headerLinkBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const renderMobileChromeBar = () => (
    <div className="ct-compact-bar-inner">
      <span className="ct-compact-title">Completed Trades</span>
      <span className="ct-compact-spacer" aria-hidden="true" />
      <Link to="/accepted-trades" className="ct-link-btn" style={headerLinkBtnStyle}>
        Accepted Trades
      </Link>
    </div>
  );

  // Helper to get pedal name from a trade's received pedal
  const getReceivedPedalName = (trade) => {
    const pedalTheyReceived = trade.theirPedal;
    if (!pedalTheyReceived) return null;
    return `${pedalTheyReceived.make || ''} ${pedalTheyReceived.model || ''}`.trim() || 'Pedal';
  };

  // 📢 Share NPD (New Pedal Day) to Reddit
  const handleShareToReddit = (trade) => {
    const pedalName = getReceivedPedalName(trade);
    if (!pedalName) {
      console.error('No received pedal data');
      return;
    }
    
    const title = `NPD: ${pedalName}`;
    const postBody = `Just completed a trade on PedalxChange.com and got this ${pedalName}!\n\nCheck out PedalxChange for trading guitar pedals: https://pedalxchange.com`;
    
    // Copy body text to clipboard as fallback
    navigator.clipboard.writeText(postBody).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    alert('Your post body has been copied to your clipboard! You can paste it into the text field on Reddit.');
    
    const redditUrl = `https://www.reddit.com/r/guitarpedals/submit?type=self&title=${encodeURIComponent(title)}&selftext=${encodeURIComponent(postBody)}`;
    const newWindow = window.open(redditUrl, '_blank');
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = redditUrl;
    }
  };

  // 📢 Share NPD to Facebook
  const handleShareToFacebook = (trade) => {
    const pedalName = getReceivedPedalName(trade);
    if (!pedalName) {
      console.error('No received pedal data');
      return;
    }
    
    const postText = `NPD: ${pedalName}! Just completed a trade on PedalxChange.com and got this ${pedalName}!`;
    const shareUrl = 'https://pedalxchange.com';
    
    // Copy text to clipboard since Facebook doesn't allow pre-filling post text
    navigator.clipboard.writeText(postText).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    alert('Your "New Pedal Day" message has been copied to your clipboard! You can paste it into your Facebook post.');
    
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const newWindow = window.open(fbUrl, '_blank', 'width=600,height=400');
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = fbUrl;
    }
  };

  const requestedUserIdsRef = useRef(new Set());
  const fetchUsernames = async (tradesList) => {
    // Get both fromUserId and toUserId from all trades
    const fromUserIds = tradesList.map(t => t.fromUserId).filter(Boolean);
    const toUserIds = tradesList.map(t => t.toUserId).filter(Boolean);
    
    // Also include current user ID to fetch their username
    const currentUserId = auth.currentUser?.uid;
    const allUserIds = [...fromUserIds, ...toUserIds];
    if (currentUserId && !allUserIds.includes(currentUserId)) {
      allUserIds.push(currentUserId);
    }
    
    const uniqueUserIds = [...new Set(allUserIds)];
    console.log('🔍 CompletedTrades - Fetching usernames for:', uniqueUserIds);
    const newUsernames = {};

    // Parallel, and each profile fetched once per page life: same treatment
    // as the Offers hub (8f972cf). The old serial loop's "already fetched?"
    // check read stale closure state, so every live-listener fire re-fetched
    // every profile one at a time.
    const toFetch = uniqueUserIds.filter((uid) => !requestedUserIdsRef.current.has(uid));
    if (toFetch.length === 0) return;
    toFetch.forEach((uid) => requestedUserIdsRef.current.add(uid));

    const results = await Promise.all(toFetch.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'publicProfiles', uid));
        return [uid, userDoc.exists() ? userDoc.data().username || 'Unknown' : 'Unknown'];
      } catch (err) {
        console.error('Error fetching username for', uid, err);
        requestedUserIdsRef.current.delete(uid); // let a later update retry
        return [uid, 'Unknown'];
      }
    }));

    Object.assign(newUsernames, Object.fromEntries(results));
    setUsernames((prev) => ({...prev, ...newUsernames}));
  };

  useEffect(() => {
    let unsubscribeFrom;
    let unsubscribeTo;
    let unsubscribeAuth;
    unsubscribeAuth = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (!user) { setNotLoggedIn(true); setLoading(false); return; }
      const userId = user.uid;

      // Query for completed trades where user is the initiator
      const fromQuery = query(
        collection(db, 'tradeOffers'),
        where('status', 'in', ['complete', 'completed']),
        where('fromUserId', '==', userId)
      );
      
      // Query for completed trades where user is the responder
      const toQuery = query(
        collection(db, 'tradeOffers'),
        where('status', 'in', ['complete', 'completed']),
        where('toUserId', '==', userId)
      );

      // Purchase offers are now handled in CompletedPurchases.jsx

      // Listen to trade queries only (purchases handled in CompletedPurchases.jsx)
      unsubscribeFrom = onSnapshot(fromQuery, async (fromSnapshot) => {
        unsubscribeTo = onSnapshot(toQuery, async (toSnapshot) => {
          console.log('🔍 CompletedTrades - From trades:', fromSnapshot.docs.length);
          console.log('🔍 CompletedTrades - To trades:', toSnapshot.docs.length);
          
          // Combine trade snapshots only
          const allDocs = [...fromSnapshot.docs, ...toSnapshot.docs];
          console.log('🔍 CompletedTrades - Total completed trades:', allDocs.length);
          
          const trades = await Promise.all(allDocs.map(async docSnap => {
            const trade = { id: docSnap.id, ...docSnap.data() };
            
            // Handle trade offer only (purchases handled in CompletedPurchases.jsx)
            // 📸 TRADE HISTORY PRESERVATION: Use pedal snapshots if available (for newer trades)
            // This prevents completed trade images from changing when users update their pedals
            let initiatorPedals = [];
            let responderPedal = null;
            
            if (trade.pedalSnapshots?.initiatorPedal && trade.pedalSnapshots?.responderPedal) {
              console.log('📸 Using pedal snapshots for completed trade display');
              initiatorPedals = (trade.pedalSnapshots.initiatorPedals && Array.isArray(trade.pedalSnapshots.initiatorPedals))
                ? trade.pedalSnapshots.initiatorPedals
                : [trade.pedalSnapshots.initiatorPedal];
              responderPedal = trade.pedalSnapshots.responderPedal;
            } else {
              // Fallback to live data for older trades without snapshots
              console.log('🔄 Fetching live pedal data for older completed trade');
              const allInitiatorIds = getInitiatorPedalIds(trade);
              const initiatorSnaps = await Promise.all(
                allInitiatorIds.map(pid => getDoc(doc(db, 'listings', pid)))
              );
              initiatorPedals = initiatorSnaps
                .filter(s => s.exists())
                .map(s => ({ id: s.id, ...s.data() }));
              const responderPedalSnap = await getDoc(doc(db, 'listings', trade.responderPedalId));
              responderPedal = responderPedalSnap.exists() ? responderPedalSnap.data() : null;
            }
            
            // Determine which pedals belong to the current user
            const isInitiator = user?.uid === trade.fromUserId;
            const myPedals = isInitiator ? initiatorPedals : (responderPedal ? [responderPedal] : []);
            const theirPedals = isInitiator ? (responderPedal ? [responderPedal] : []) : initiatorPedals;
            const myPedal = myPedals[0] || null;
            const theirPedal = theirPedals[0] || null;
            
            return { ...trade, myPedal, theirPedal, myPedals, theirPedals };
          }));
          
          // Sort by acceptance date (from history), fallback to createdAt
          trades.sort((a, b) => {
            // Find the acceptance timestamp from history
            const getAcceptanceDate = (trade) => {
              if (trade.history && Array.isArray(trade.history)) {
                const acceptedEntry = [...trade.history].reverse().find(h => 
                  h.message && h.message.toLowerCase().includes('accepted')
                );
                if (acceptedEntry && acceptedEntry.timestamp) {
                  return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
                }
              }
              return trade.createdAt?.toDate?.() || new Date(0);
            };
            
            const aDate = getAcceptanceDate(a);
            const bDate = getAcceptanceDate(b);
            return bDate - aDate;
          });
          
          console.log('✅ Completed trades loaded:', trades.length);
          setCompletedTrades(trades);
          setLoading(false);
          
          // Fetch usernames for all users in trades
          if (trades.length > 0) {
            fetchUsernames(trades);
          }
        });
      });
    });
    
    return () => {
      if (unsubscribeFrom) unsubscribeFrom();
      if (unsubscribeTo) unsubscribeTo();
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []);

  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to View Completed Trades</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to view your completed trades.</p>
          <Link to="/login" style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading completed trades...</div>;

  return (
    <div className="min-h-screen" onClickCapture={() => saveView('list')} style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div
        className="completed-trades-page"
        style={{
          backgroundColor: '#F7F3EB',
          flex: 1,
          flexDirection: 'column',
          padding: '0 1rem',
          display: 'flex',
        }}
      >
      <div className="completed-trades-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem', flex: 1 }}>
        <div className={`ct-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
          <style>{`
            .ct-compact-bar-inner {
              max-width: 1100px;
              margin: 0 auto;
              width: 100%;
              box-sizing: border-box;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 12px;
            }
            .ct-compact-title {
              font-family: Nunito, sans-serif;
              font-weight: 700;
              font-size: 1.125rem;
              color: #111827;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
              flex: 0 0 auto;
              text-align: left;
            }
            .ct-compact-spacer {
              flex: 1 1 auto;
              min-width: 0;
            }
            .ct-sticky-header .ct-link-btn {
              flex-shrink: 0;
            }
            .ct-sticky-header .ct-header-expanded {
              display: block;
            }
            .ct-sticky-header .ct-mobile-chrome-slot {
              display: none;
            }
            .ct-sticky-header .ct-compact-bar-mobile-fixed {
              display: none;
            }
            @media (min-width: 769px) {
              .ct-sticky-header .ct-compact-bar-mobile-fixed {
                display: none !important;
              }
              .ct-sticky-header .ct-compact-bar {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.7rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
              }
              .ct-sticky-header.is-compact .ct-compact-bar {
                opacity: 1;
                pointer-events: auto;
              }
              .ct-sticky-header .ct-link-btn {
                padding: 0.6rem 1.4rem !important;
              }
            }
            @media (max-width: 768px) {
              .completed-trades-page-content {
                padding-top: 0 !important;
              }
              .ct-sticky-header .ct-header-expanded {
                display: none !important;
              }
              .ct-sticky-header .ct-mobile-chrome-slot {
                display: block !important;
                position: relative;
                flex-shrink: 0;
                background-color: #F7F3EB;
                overflow: hidden;
                margin-left: -0.75rem;
                margin-right: -0.75rem;
                width: calc(100% + 1.5rem);
              }
              .ct-sticky-header .ct-compact-bar-inflow {
                display: block;
                padding: 0.6rem 1rem 1.5rem 1rem;
                background-color: #F7F3EB;
                box-sizing: border-box;
              }
              .ct-sticky-header .ct-compact-bar-inflow.is-suppressed {
                visibility: hidden;
                pointer-events: none;
              }
              .ct-sticky-header .ct-compact-bar-desktop {
                display: none !important;
              }
              .ct-sticky-header .ct-compact-bar-mobile-fixed {
                display: block;
              }
              .ct-sticky-header .ct-compact-bar {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.85rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                pointer-events: none;
                transform: translateY(-100%);
                transition: transform 0.2s ease;
              }
              .ct-sticky-header .ct-compact-bar.is-mobile-visible {
                display: block;
                transform: translateY(0);
                pointer-events: auto;
              }
              .ct-sticky-header .ct-compact-title {
                font-size: calc(1rem + 1pt) !important;
                color: #5A9ECB !important;
              }
              .ct-sticky-header .ct-link-btn {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>

          <div
            ref={mobileChromeSlotRef}
            className="ct-mobile-chrome-slot"
            style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
          >
            <div
              ref={mobileChromeInflowRef}
              className={['ct-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
            >
              {renderMobileChromeBar()}
            </div>
          </div>

          <div
            className={[
              'ct-compact-bar',
              showMobileChromeBar && 'is-mobile-visible',
            ].filter(Boolean).join(' ')}
          >
            <div className="ct-compact-bar-mobile-fixed">
              {renderMobileChromeBar()}
            </div>
            <div className="ct-compact-bar-desktop">
              {renderMobileChromeBar()}
            </div>
          </div>

          <div className="ct-header-expanded">
            <Link
              to="/trading"
              className="completed-trades-header-back-link"
              style={{
                color: '#5A9ECB',
                textDecoration: 'none',
                alignItems: 'center',
                marginBottom: '1rem',
                fontSize: '1.125rem'
              }}
            >
              ← Back to Trading
            </Link>
            <div className="completed-trades-header-expanded-inner">
              <div className="completed-trades-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Completed Trades
                </h1>
                <p className="completed-trades-header-description text-gray-600" style={{ fontSize: '17px' }}>
                  {completedTrades.length} completed trade{completedTrades.length !== 1 ? 's' : ''} • Sorted by most recent
                </p>
              </div>
              <div className="completed-trades-header-links">
                <Link to="/accepted-trades" className="ct-link-btn" style={headerLinkBtnStyle}>
                  Accepted Trades
                </Link>
              </div>
            </div>
          </div>
        </div>

      <style jsx>{`
        @media (min-width: 769px) {
          .completed-trades-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .completed-trades-header-back-link {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex !important;
            align-items: center;
          }
          .completed-trades-header-expanded-inner {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem 5rem;
            margin-bottom: 1.5rem;
          }
          .completed-trades-header-copy {
            flex: 0 1 auto;
            min-width: 0;
          }
          .completed-trades-header-links {
            flex: 0 0 auto;
            margin-left: auto;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.5rem;
            align-self: flex-end;
          }
          .completed-trades-header-expanded-inner h1 {
            text-align: left !important;
            margin: 0 !important;
          }
          .completed-trades-header-description {
            margin: 0 !important;
            padding: 0 !important;
            text-align: left !important;
          }
          .completed-trades-list {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
            align-items: stretch !important;
          }
          .completed-trades-empty {
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
          .completed-trade-card {
            width: 100% !important;
            max-width: none !important;
          }
        }
        @media (max-width: 768px) {
          .completed-trades-page {
            padding: 0 0.75rem !important;
          }
          .completed-trades-list {
            padding: 0 !important;
          }
          .completed-trades-empty {
            padding: 0 !important;
          }
        }
      `}</style>
      {completedTrades.length === 0 ? (
        <p className='text-gray-600 completed-trades-empty' style={{ maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>You have no completed trades.</p>
      ) : (
        <div className="completed-trades-list" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {completedTrades.map((trade) => {
            const isFromUser = trade.fromUserId === auth.currentUser?.uid;
            const userPaid = isFromUser 
              ? trade.paymentStatus?.fromUserPaid 
              : trade.paymentStatus?.toUserPaid;
            
            // Get acceptance date from history
            const getAcceptanceDate = (trade) => {
              if (trade.history && Array.isArray(trade.history)) {
                const acceptedEntry = [...trade.history].reverse().find(h => 
                  h.message && h.message.toLowerCase().includes('accepted')
                );
                if (acceptedEntry && acceptedEntry.timestamp) {
                  return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
                }
              }
              return trade.createdAt?.toDate?.() || new Date();
            };
            
            const tradeDate = getAcceptanceDate(trade);
            
            return (
            <div 
              key={trade.id}
              data-restore-anchor={trade.id}
              className="completed-trade-card"
              style={{ 
                borderRadius: '12px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                backgroundColor: 'white',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <style jsx>{`
                @media (min-width: 769px) {
                  .completed-single-pedal-col {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                  }
                }
                @media (max-width: 768px) {
                  .completed-trade-card {
                    width: 100% !important;
                    padding: 1rem !important;
                    margin: 0 0 1rem 0 !important;
                  }
                  .completed-pedal-inner {
                    flex-direction: column !important;
                  }
                  .completed-trade-image {
                    width: 100% !important;
                    max-width: 180px !important;
                    height: auto !important;
                    aspect-ratio: 1 !important;
                  }
                  .completed-trade-swap-desktop {
                    display: none !important;
                  }
                  .completed-trade-mobile-divider {
                    display: block !important;
                  }
                  .completed-trade-buttons {
                    justify-content: space-between !important;
                    width: 100% !important;
                  }
                  .completed-trade-buttons a {
                    text-align: center !important;
                  }
                  .completed-trade-share-desktop {
                    display: none !important;
                  }
                  .completed-trade-share-mobile {
                    display: block !important;
                  }
                  .completed-mobile-username {
                    display: block !important;
                  }
                  .completed-desktop-username {
                    display: none !important;
                  }
                  .completed-trade-header {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.5rem !important;
                  }
                  .completed-trade-header > div {
                    justify-content: flex-start !important;
                  }
                  .completed-trade-date-long {
                    display: none !important;
                  }
                  .completed-trade-date-short {
                    display: inline !important;
                  }
                  .completed-multi-image {
                    width: 52px !important;
                    height: 52px !important;
                  }
                }
              `}</style>

              {/* Top row: Date + Status pills */}
              <div className="completed-trade-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '0.9rem', color: '#6b7280', fontFamily: 'Nunito', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  <span className="completed-trade-date-long">{tradeDate ? tradeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                  <span className="completed-trade-date-short" style={{ display: 'none' }}>{tradeDate ? tradeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                </span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {trade.cashAmount > 0 && (
                    <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #C9A227', color: '#946F00', backgroundColor: 'rgba(201, 162, 39, 0.08)' }}>
                      Cash: ${trade.cashAmount.toFixed(0)} offered
                    </span>
                  )}
                  <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #a7c4a0', color: '#5a7d53', backgroundColor: 'rgba(90, 125, 83, 0.07)' }}>
                    {userPaid ? 'Paid' : 'Unpaid'}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #86efac', color: '#166534', backgroundColor: 'rgba(22, 101, 52, 0.06)' }}>
                    Completed
                  </span>
                </div>
              </div>

              {/* Pedals row */}
              <div style={{ display: 'flex', alignItems: 'stretch', padding: '1rem 0' }}>
                {/* My Pedal(s) */}
                <div className={trade.theirPedals && trade.theirPedals.length > 1 ? 'completed-single-pedal-col' : ''} style={{ flex: 1 }}>
                  {/* Mobile-only username above image */}
                  <div className="completed-mobile-username" style={{ display: 'none', fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '6px' }}>
                    {usernames[auth.currentUser?.uid] || 'Loading...'} (you)
                  </div>
                  {trade.myPedals && trade.myPedals.length > 1 ? (
                    <div>
                      <div className="completed-multi-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {trade.myPedals.map((pedal, idx) => (
                          <Link
                            key={pedal?.id || idx}
                            className="completed-multi-link"
                            to={`/archived-listing/trade/${trade.id}/my${pedal?.id ? `/${pedal.id}` : ''}`}
                            style={{ display: 'flex', gap: '10px', alignItems: idx === 0 ? 'flex-start' : 'center', textDecoration: 'none', color: 'inherit' }}
                          >
                            {(pedal?.images?.top || placeholderFor(pedal || {}).url) ? (
                              <img src={pedal?.images?.top || placeholderFor(pedal || {}).url} alt={`${pedal.make} ${pedal.model}`}
                                className="completed-multi-image"
                                style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', backgroundColor: '#f3f4f6', flexShrink: 0 }} />
                            ) : (
                              <div className="completed-multi-image" style={{ width: '64px', height: '64px', backgroundColor: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No Image</span>
                              </div>
                            )}
                            <div className="completed-multi-text" style={{ textAlign: 'left' }}>
                              {idx === 0 && (
                                <div className="completed-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                                  {usernames[auth.currentUser?.uid] || 'Loading...'} (you)
                                </div>
                              )}
                              <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.make}</span>{' '}
                              <span style={{ fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.model}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                  <div className="completed-pedal-inner" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {(trade.myPedal?.images?.top || placeholderFor(trade.myPedal || {}).url) ? (
                      <img
                        src={trade.myPedal?.images?.top || placeholderFor(trade.myPedal || {}).url}
                        alt='your pedal'
                        className="completed-trade-image"
                        style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#f3f4f6', flexShrink: 0 }}
                      />
                    ) : (
                      <div className="completed-trade-image" style={{ width: '120px', height: '120px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No Image</span>
                      </div>
                    )}
                    <div style={{ textAlign: 'left' }}>
                      {/* Desktop-only username inline with details */}
                      <div className="completed-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                        {usernames[auth.currentUser?.uid] || 'Loading...'} (you)
                      </div>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito' }}>{trade.myPedal?.make}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'Nunito' }}>{trade.myPedal?.model}</div>
                      <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito', marginTop: '2px' }}>
                        {trade.myPedal?.condition} · ${trade.myPedal?.price}
                      </div>
                      {trade.myPedal?.id && (
                        <Link
                          to={`/archived-listing/trade/${trade.id}/my`}
                          style={{ fontSize: '0.8rem', color: '#5A9ECB', textDecoration: 'none', fontFamily: 'Nunito', display: 'inline-block', marginTop: '4px' }}
                        >
                          View listing →
                        </Link>
                      )}
                    </div>
                  </div>
                  )}
                </div>

                {/* Vertical center divider with swap icon (desktop) / plain line (mobile) */}
                <div className="completed-trade-swap-desktop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '0 1rem', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px', backgroundColor: '#e5e7eb' }}></div>
                  <span style={{ fontSize: '1.25rem', color: '#9ca3af', position: 'relative', backgroundColor: 'white', padding: '4px 0' }}>⇄</span>
                </div>
                {/* Mobile center divider (just a vertical line, no icon) */}
                <div className="completed-trade-mobile-divider" style={{ display: 'none', flexShrink: 0, width: '1px', backgroundColor: '#e5e7eb', margin: '0 0.5rem' }}></div>

                {/* Their Pedal(s) */}
                <div className={trade.myPedals && trade.myPedals.length > 1 ? 'completed-single-pedal-col' : ''} style={{ flex: 1 }}>
                  {/* Mobile-only username above image */}
                  <div className="completed-mobile-username" style={{ display: 'none', fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '6px' }}>
                    {usernames[isFromUser ? trade.toUserId : trade.fromUserId] || 'Loading...'}
                  </div>
                  {trade.theirPedals && trade.theirPedals.length > 1 ? (
                    <div>
                      <div className="completed-multi-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {trade.theirPedals.map((pedal, idx) => (
                          <Link
                            key={pedal?.id || idx}
                            className="completed-multi-link"
                            to={`/archived-listing/trade/${trade.id}/their${pedal?.id ? `/${pedal.id}` : ''}`}
                            style={{ display: 'flex', gap: '10px', alignItems: idx === 0 ? 'flex-start' : 'center', textDecoration: 'none', color: 'inherit' }}
                          >
                            {(pedal?.images?.top || placeholderFor(pedal || {}).url) ? (
                              <img src={pedal?.images?.top || placeholderFor(pedal || {}).url} alt={`${pedal.make} ${pedal.model}`}
                                className="completed-multi-image"
                                style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', backgroundColor: '#f3f4f6', flexShrink: 0 }} />
                            ) : (
                              <div className="completed-multi-image" style={{ width: '64px', height: '64px', backgroundColor: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No Image</span>
                              </div>
                            )}
                            <div className="completed-multi-text" style={{ textAlign: 'left' }}>
                              {idx === 0 && (
                                <div className="completed-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                                  {usernames[isFromUser ? trade.toUserId : trade.fromUserId] || 'Loading...'}
                                </div>
                              )}
                              <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.make}</span>{' '}
                              <span style={{ fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.model}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                  <div className="completed-pedal-inner" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {(trade.theirPedal?.images?.top || placeholderFor(trade.theirPedal || {}).url) ? (
                      <img
                        src={trade.theirPedal?.images?.top || placeholderFor(trade.theirPedal || {}).url}
                        alt='their pedal'
                        className="completed-trade-image"
                        style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#f3f4f6', flexShrink: 0 }}
                      />
                    ) : (
                      <div className="completed-trade-image" style={{ width: '120px', height: '120px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No Image</span>
                      </div>
                    )}
                    <div style={{ textAlign: 'left' }}>
                      {/* Desktop-only username inline with details */}
                      <div className="completed-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                        {usernames[isFromUser ? trade.toUserId : trade.fromUserId] || 'Loading...'}
                      </div>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito' }}>{trade.theirPedal?.make}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'Nunito' }}>{trade.theirPedal?.model}</div>
                      <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito', marginTop: '2px' }}>
                        {trade.theirPedal?.condition} · ${trade.theirPedal?.price}
                      </div>
                      {trade.theirPedal?.id && (
                        <Link
                          to={`/archived-listing/trade/${trade.id}/their`}
                          style={{ fontSize: '0.8rem', color: '#5A9ECB', textDecoration: 'none', fontFamily: 'Nunito', display: 'inline-block', marginTop: '4px' }}
                        >
                          View listing →
                        </Link>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* Bottom section: Buttons + Share links */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                <div className="completed-trade-buttons" style={{ display: 'flex', gap: '10px' }}>
                  <Link
                    to={`/trade-progress/${trade.id}`}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '9999px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: '#374151',
                      fontWeight: '700',
                      fontSize: '0.9rem',
                      fontFamily: 'Nunito',
                      textDecoration: 'none',
                      display: 'inline-block',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    View progress
                  </Link>
                  {isMessagingAvailable(trade) && (
                    <Link
                      to={`/trade-messages/${trade.id}`}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '9999px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#374151',
                        fontWeight: '700',
                        fontSize: '0.9rem',
                        fontFamily: 'Nunito',
                        textDecoration: 'none',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Messages
                    </Link>
                  )}
                  {/* Desktop share links inline */}
                  <div className="completed-trade-share-desktop" style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
                    <button
                      onClick={() => handleShareToReddit(trade)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', fontFamily: 'Nunito', color: '#FF4500', whiteSpace: 'nowrap' }}
                    >
                      Share on Reddit
                    </button>
                    <button
                      onClick={() => handleShareToFacebook(trade)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', fontFamily: 'Nunito', color: '#1877F2', whiteSpace: 'nowrap' }}
                    >
                      Share on Facebook
                    </button>
                  </div>
                </div>
                {/* Mobile: divider + share links below buttons */}
                <div className="completed-trade-share-mobile" style={{ display: 'none' }}>
                  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '0.75rem', paddingTop: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}>
                      <button
                        onClick={() => handleShareToReddit(trade)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', fontFamily: 'Nunito', color: '#FF4500', padding: 0 }}
                      >
                        Share on Reddit
                      </button>
                      <button
                        onClick={() => handleShareToFacebook(trade)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', fontFamily: 'Nunito', color: '#1877F2', padding: 0 }}
                      >
                        Share on Facebook
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )})}
        </div>
      )}

      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CompletedTrades; 